import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Separator } from "../ui/separator";
import { Kbd, KbdGroup } from "../ui/kbd";

function getModKeyLabel() {
  if (typeof navigator === "undefined") return "Ctrl";
  const platform = navigator.platform || "";
  const userAgent = navigator.userAgent || "";
  const isApple = /Mac|iPhone|iPad|iPod/i.test(platform) || /Mac OS X/i.test(userAgent);
  return isApple ? "⌘" : "Ctrl";
}

function ShortcutRow({ label, keys }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <div className="text-sm text-foreground">{label}</div>
      <div className="flex items-center gap-3">
        {keys.map((keyGroup, idx) => (
          <KbdGroup key={idx}>
            {keyGroup.map((key) => (
              <Kbd key={key}>{key}</Kbd>
            ))}
          </KbdGroup>
        ))}
      </div>
    </div>
  );
}

export function KeyboardShortcutsDialog({ open, onOpenChange }) {
  const { t } = useTranslation("common");
  const modKey = useMemo(() => getModKeyLabel(), []);

  const shortcuts = useMemo(
    () => [
      { label: t("label.Toggle sidebar"), keys: [[modKey, "B"]] },
      { label: t("label.Open keyboard shortcuts"), keys: [["?"], [modKey, "/"]] },
      { label: t("label.Close"), keys: [["Esc"]] },
    ],
    [modKey, t]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("title.Keyboard shortcuts")}</DialogTitle>
          <DialogDescription>
            {t("description.Use keyboard shortcuts to navigate faster")}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          {shortcuts.map((shortcut, idx) => (
            <React.Fragment key={shortcut.label}>
              {idx > 0 && <Separator />}
              <ShortcutRow label={shortcut.label} keys={shortcut.keys} />
            </React.Fragment>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

